import { formatCurrency, hasValue, utc } from '@vrplatform/utils';

export const TABLE_PRIMITIVE_VALUE_PLACEHOLDER = '—';

export type TablePrimitiveDateInput = Date | string;

export type TablePrimitiveDateRangeValue = {
  end: TablePrimitiveDateInput | null | undefined;
  start: TablePrimitiveDateInput | null | undefined;
};

export type TablePrimitiveAddressValue = {
  city: string;
  country?: string | null | undefined;
  countryCode?: string | null | undefined;
  line1: string;
  line2?: string | null | undefined;
  postcode?: string | null | undefined;
  state?: string | null | undefined;
  stateCode?: string | null | undefined;
};

export type FormatTablePrimitiveNumberOptions = {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  useGrouping?: boolean;
  value: number | string | null | undefined;
};

export const formatTablePrimitiveDate = (
  value: TablePrimitiveDateInput | null | undefined,
  placeholder = TABLE_PRIMITIVE_VALUE_PLACEHOLDER
) => {
  if (!value) return placeholder;

  return utc(value).format('MMM D, YYYY');
};

export const formatTablePrimitiveDateRange = (
  value: TablePrimitiveDateRangeValue | null | undefined,
  placeholder = TABLE_PRIMITIVE_VALUE_PLACEHOLDER
) => {
  if (!value?.start || !value?.end) return placeholder;

  const start = utc(value.start);
  const end = utc(value.end);

  if (start.year() !== end.year()) {
    return `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`;
  }

  if (start.month() !== end.month()) {
    return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
  }

  return `${start.format('MMM DD')} - ${end.format('DD, YYYY')}`;
};

export const formatTablePrimitiveAccounting = ({
  currency = 'USD',
  placeholder = TABLE_PRIMITIVE_VALUE_PLACEHOLDER,
  value,
}: {
  currency?: string;
  placeholder?: string;
  value: number | string | null | undefined;
}) => {
  if (!hasValue(value) || value === '') return placeholder;
  if (typeof value === 'string') return value;

  return formatCurrency(value, currency);
};

export const formatTablePrimitiveNumber = ({
  maximumFractionDigits,
  minimumFractionDigits,
  placeholder = TABLE_PRIMITIVE_VALUE_PLACEHOLDER,
  prefix,
  suffix,
  useGrouping = true,
  value,
}: FormatTablePrimitiveNumberOptions) => {
  if (!hasValue(value) || value === '') return placeholder;
  if (typeof value === 'string') return value;

  const formatted = new Intl.NumberFormat(undefined, {
    maximumFractionDigits,
    minimumFractionDigits,
    useGrouping,
  }).format(value);

  return `${prefix || ''}${formatted}${suffix || ''}`;
};

export const formatTablePrimitiveAddress = (
  value: TablePrimitiveAddressValue | string | null | undefined,
  placeholder = TABLE_PRIMITIVE_VALUE_PLACEHOLDER
) => {
  if (!value) return placeholder;
  if (typeof value === 'string') return value;

  const formatted = [
    value.line1,
    value.line2,
    value.city,
    value.state || value.stateCode,
    value.countryCode || value.country,
  ]
    .filter(
      (part): part is string => typeof part === 'string' && part.length > 0
    )
    .join(', ');

  return formatted || placeholder;
};
